import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft,
  BarChart3, 
  Bell, 
  FileText,
  RefreshCw,
  Shield,
  Smartphone,
  BookOpen,
  TrendingUp,
  Trophy,
  Users,
  MoreHorizontal,
  X,
  Home,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSafeBack } from '../hooks/useSafeBack';

const NAV_ITEMS = [
  { path: '/admin/analytics',              icon: BarChart3,   label: 'Analytics',     testId: 'admin-nav-analytics' },
  { path: '/admin/notifications',           icon: Bell,        label: 'Notifications', testId: 'admin-nav-notifications' },
  { path: '/admin/hosts',                  icon: Users,       label: 'Host Intelligence', testId: 'admin-nav-hosts' },
  { path: '/admin/notification-audit',      icon: Smartphone,  label: 'Audit Log',     testId: 'admin-nav-audit' },
  { path: '/admin/game-tape',              icon: FileText,    label: 'Game Tape',     testId: 'admin-nav-game-tape' },
  { path: '/admin/notification-templates', icon: BookOpen,    label: 'Templates',     testId: 'admin-nav-templates' },
  { path: '/admin/notification-performance', icon: TrendingUp, label: 'Performance',   testId: 'admin-nav-performance' },
  { path: '/rankings',                     icon: Trophy,      label: 'Leaderboard',   testId: 'admin-nav-leaderboard' },
];

export default function AdminHeader({ title, onRefresh, refreshing = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { goBack } = useSafeBack('/admin');
  const [moreOpen, setMoreOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div
      className="bg-white border-b sticky top-0 z-30 text-gray-900"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <div className="flex items-center justify-between px-2 pb-2 md:px-4 md:pb-3 gap-2">
        {/* Left: back + home + title — constrained so it doesn't push nav off-screen */}
        <div className="flex items-center gap-1 min-w-0 shrink">
          <button 
            onClick={goBack}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            data-testid="admin-nav-back"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            data-testid="admin-nav-home"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-gray-500" />
          </button>
          <div className="flex items-center gap-1 min-w-0">
            <Shield className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <h1 className="text-sm font-bold text-gray-900 truncate max-w-[80px] md:max-w-[160px] lg:max-w-none lg:text-base">{title || 'ADMIN'}</h1>
          </div>
        </div>
        
        {/* Right: nav buttons — never shrink */}
        <div className="flex items-center flex-shrink-0">
          {/* Desktop (>=1024px): all items with labels */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              return (
                <Button 
                  key={item.path}
                  variant={isActive(item.path) ? 'default' : 'ghost'} 
                  size="sm" 
                  onClick={() => navigate(item.path)}
                  className="h-8 px-2.5 text-xs whitespace-nowrap"
                  data-testid={item.testId}
                >
                  <Icon className="w-3.5 h-3.5 mr-1.5" />
                  {item.label}
                </Button>
              );
            })}
            {onRefresh && (
              <Button variant="ghost" size="sm" className="h-8 px-2.5 text-xs" onClick={onRefresh}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>

          {/* Tablet (768-1023px): icon-only buttons for all items */}
          <div className="hidden md:flex lg:hidden items-center gap-0.5">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`p-2 rounded-lg transition-colors
                    ${isActive(item.path) ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}
                  data-testid={item.testId}
                  aria-label={item.label}
                  title={item.label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>

          {/* Mobile (<768px): 2 icon buttons + More dropdown */}
          <div className="flex md:hidden items-center gap-0.5">
            {/* Show first 2 as icon-only */}
            {NAV_ITEMS.slice(0, 2).map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`p-2 rounded-lg transition-colors
                    ${isActive(item.path) ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                  data-testid={item.testId}
                  aria-label={item.label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}

            {/* More button — contains remaining items */}
            <div className="relative">
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                className={`p-2 rounded-lg transition-colors
                  ${moreOpen || NAV_ITEMS.slice(2).some(i => isActive(i.path)) ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                data-testid="admin-nav-more"
                aria-label="More pages"
              >
                {moreOpen ? <X className="w-4 h-4" /> : <MoreHorizontal className="w-4 h-4" />}
              </button>

              {moreOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border z-50 py-1 overflow-hidden" data-testid="admin-nav-more-menu">
                    {NAV_ITEMS.slice(2).map(item => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { navigate(item.path); setMoreOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                            ${isActive(item.path) ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
                          data-testid={item.testId}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {item.label}
                        </button>
                      );
                    })}
                    {onRefresh && (
                      <button
                        onClick={() => { onRefresh(); setMoreOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 border-t"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
